import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_STYLES = { active: 'bg-green-100 text-green-700', suspended: 'bg-amber-100 text-amber-700', disabled: 'bg-gray-200 text-gray-600', deleted: 'bg-red-100 text-red-700' };

export default function TenantList() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [deleteModal, setDeleteModal] = useState(null); // { code, name, mode: 'soft'|'hard' }
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (planFilter) params.set('plan', planFilter);
      const { data } = await api.get(`/tenants?${params}`);
      setTenants(data.data || []);
    } catch {} finally { setLoading(false); }
  }, [search, statusFilter, planFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleStatus = async (code, currentStatus) => {
    const next = currentStatus === 'active' ? 'suspended' : 'active';
    if (!window.confirm(`${next === 'suspended' ? 'Suspend' : 'Activate'} tenant "${code}"?`)) return;
    try {
      await api.patch(`/tenants/${code}/status`, { status: next });
      fetch();
    } catch {}
  };

  const testConn = async (code) => {
    try {
      const { data } = await api.post(`/tenants/${code}/test-connection`);
      const d = data.data;
      alert(d.connected ? `Connected! DB: ${d.database}, Tables: ${d.tables}, Users: ${d.users}` : `Failed: ${d.error}`);
    } catch { alert('Error testing connection'); }
  };

  const openDeleteModal = (code, name) => setDeleteModal({ code, name, mode: 'soft' });

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/tenants/${deleteModal.code}?mode=${deleteModal.mode}`);
      showToast(data.message);
      setDeleteModal(null);
      fetch();
    } catch (err) {
      showToast(err.response?.data?.message || 'Delete failed', 'error');
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Tenant Management</h2>
          <p className="text-sm text-gray-500 mt-0.5">{tenants.length} organization(s) registered</p>
        </div>
        <button onClick={() => navigate('/tenants/new')} className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          <span>Add Tenant</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex flex-wrap gap-3">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, code, or email..." className="flex-1 min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="disabled">Disabled</option>
          <option value="deleted">Deleted</option>
        </select>
        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All Plans</option>
          <option value="basic">Basic</option>
          <option value="standard">Standard</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-10 text-gray-400">No tenants found</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Organization', 'Code', 'Status', 'Plan', 'Subscription', 'Admin Email', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tenants.map(t => (
                  <tr key={t.code} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600" onClick={() => navigate(`/tenants/${t.code}`)}>{t.name}</div>
                      {t.city && <div className="text-xs text-gray-400">{t.city}{t.state ? `, ${t.state}` : ''}</div>}
                    </td>
                    <td className="px-4 py-3"><span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{t.code}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[t.status]}`}>{t.status}</span></td>
                    <td className="px-4 py-3"><span className="text-sm capitalize text-gray-700">{t.subscription_plan}</span></td>
                    <td className="px-4 py-3">
                      {t.subscription_expiry ? (
                        <div>
                          <div className="text-sm text-gray-700">{new Date(t.subscription_expiry).toLocaleDateString()}</div>
                          <div className={`text-xs font-medium ${t.days_until_expiry < 0 ? 'text-red-600' : t.days_until_expiry <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                            {t.days_until_expiry < 0 ? `Expired ${Math.abs(t.days_until_expiry)}d ago` : `${t.days_until_expiry}d remaining`}
                          </div>
                        </div>
                      ) : <span className="text-xs text-gray-400">Not set</span>}
                    </td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-600">{t.admin_email || '—'}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <button onClick={() => navigate(`/tenants/${t.code}`)} title="View" className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        <button onClick={() => navigate(`/tenants/${t.code}/edit`)} title="Edit" className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => testConn(t.code)} title="Test DB" className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>
                        </button>
                        <button onClick={() => toggleStatus(t.code, t.status)} title={t.status === 'active' ? 'Suspend' : 'Activate'} className={`p-1.5 rounded-lg transition-colors ${t.status === 'active' ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}>
                          {t.status === 'active' ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          )}
                        </button>
                        <button onClick={() => openDeleteModal(t.code, t.name)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-red-50 px-6 py-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Delete Tenant</h3>
                  <p className="text-xs text-gray-500">"{deleteModal.name}" ({deleteModal.code})</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">Choose how you want to delete this tenant:</p>

              {/* Soft Delete Option */}
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${deleteModal.mode === 'soft' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setDeleteModal(m => ({ ...m, mode: 'soft' }))}
              >
                <input type="radio" name="deleteMode" checked={deleteModal.mode === 'soft'} onChange={() => {}} className="mt-0.5 w-4 h-4 text-amber-600" />
                <div>
                  <div className="text-sm font-semibold text-gray-900">Soft Delete</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Mark the tenant as <span className="font-medium text-amber-700">deleted</span> but keep the database intact.
                    The tenant will be hidden from active lists and cannot log in.
                    You can restore it later by changing the status back to active.
                  </p>
                </div>
              </label>

              {/* Hard Delete Option */}
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${deleteModal.mode === 'hard' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setDeleteModal(m => ({ ...m, mode: 'hard' }))}
              >
                <input type="radio" name="deleteMode" checked={deleteModal.mode === 'hard'} onChange={() => {}} className="mt-0.5 w-4 h-4 text-red-600" />
                <div>
                  <div className="text-sm font-semibold text-gray-900">Hard Delete (Permanent)</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    <span className="font-medium text-red-600">Permanently delete</span> the tenant's entire database and remove all records.
                    This action <span className="font-bold text-red-700">CANNOT be undone</span>. All data including vouchers, accounts, 
                    daybooks, and user records will be destroyed.
                  </p>
                </div>
              </label>

              {deleteModal.mode === 'hard' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs text-red-700">
                    <strong>Warning:</strong> This will DROP the database <span className="font-mono bg-red-100 px-1 rounded">tenant_{deleteModal.code}_db</span> and 
                    permanently remove all associated tenant data, audit logs, and migration history. This is irreversible!
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className={`px-4 py-2 text-sm text-white rounded-lg font-medium transition-colors disabled:opacity-50 ${deleteModal.mode === 'hard' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                {deleting ? (
                  <span className="inline-flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Deleting...</span>
                ) : deleteModal.mode === 'hard' ? 'Permanently Delete' : 'Soft Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
